import { Domain, LocationOn } from '@mui/icons-material';
import {
  ItemsMapItem,
  useAdvancedNavigationTree,
} from '@smartfm/container/hooks/useAdvancedNavigationTree';
import { useBuildingIds } from '@smartfm/container/hooks/useBuildingIds';
import { useClientData } from '@smartfm/container/hooks/useClientData';
import { useClientId } from '@smartfm/container/hooks/useClientId';
import { Box } from '@smartfm/ui/components/Box';
import { Stack } from '@smartfm/ui/components/Stack';
import { Typography } from '@smartfm/ui/components/Typography';
import { DefaultBuildingButton } from '../Button/DefaultBuildingButton';

interface AppTitleProps {
  activeAppName?: string;
  defaultData?: {
    defaultClient: string;
    defaultBuildingsMap: Map<
      string,
      {
        site: string;
        default: boolean;
      }
    >;
  };
}

export function AppTitle(props: AppTitleProps) {
  const [clientId] = useClientId();
  const client = useClientData();
  const [_, buildingIds] = useBuildingIds();
  const treeQuery = useAdvancedNavigationTree({ clientId });

  const buildingsMap = treeQuery.data?.itemsMap ?? {};
  const clientText = client?.name ?? 'Loading...';
  const buildingText = getBuildingsText(buildingIds, buildingsMap);

  return (
    <Stack
      flexShrink={0}
      flexDirection={'row'}
      justifyContent={'space-between'}
      gap={2}
      className='app-title'
      sx={{ paddingX: 4, paddingY: 4, bgcolor: 'white' }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ marginBottom: 2 }}>
          <Typography variant='Calibre/H5 Headline'>
            {props.activeAppName ?? 'SmartFM Insights'}
          </Typography>
        </Box>
        <Stack
          flexDirection='row'
          gap={2}
        >
          <Stack
            gap={1}
            flexDirection={'row'}
            alignItems={'center'}
            sx={{ flexShrink: 0 }}
          >
            <Domain />
            <Typography variant='Calibre/Subtitle 2'>{clientText}</Typography>
          </Stack>
          <Stack
            gap={1}
            flexDirection={'row'}
            alignItems={'center'}
            overflow={'hidden'}
            minWidth={0}
          >
            <LocationOn />
            <Typography
              variant='Calibre/Body 2'
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {buildingText}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <DefaultBuildingButton
        clientId={clientId}
        clientName={clientText}
        buildingIds={buildingIds}
        buildingName={buildingsMap?.[buildingIds?.[0]]?.label}
        defaultData={props.defaultData}
      />
    </Stack>
  );
}

function getBuildingsText(
  ids: string[],
  buildingsMap: Record<string, ItemsMapItem>
): string {
  if (ids.length === 1) {
    const buildingData = buildingsMap[ids[0]];
    if (!buildingData) {
      return 'Loading...';
    }
    const buildingLabel = buildingData.label;
    if (buildingData.secondaryText) {
      return `${buildingLabel}: ${buildingData.secondaryText}`;
    }
    return buildingLabel;
  } else if (ids.length === 0) {
    const buildingsCount = Object.keys(buildingsMap).length;
    return `All Buildings (${buildingsCount})`;
  }

  return `Multiple Buildings (${ids.length})`;
}
